<template>
  <div class="fmt-tool">
    <div class="fmt-toolbar">
      <span class="fmt-label">{{ $t('tool.CompoundInterest.title') }}</span>
      <span class="fmt-spacer"/>
      <el-button size="small" plain icon="el-icon-delete" @click="clearAll">{{ $t("common.action.clear") }}</el-button>
    </div>

    <!-- 输入 -->
    <div class="ci-form">
      <div class="ci-field">
        <span class="ci-field-lbl">{{ $t('tool.CompoundInterest.principal') }}</span>
        <el-input v-model.number="principal" type="number" size="medium" :min="0" :placeholder="$t('tool.CompoundInterest.phPrincipal')" @input="calc">
          <template slot="prepend">¥</template>
        </el-input>
      </div>
      <div class="ci-field">
        <span class="ci-field-lbl">{{ $t('tool.CompoundInterest.rate') }}</span>
        <el-input v-model.number="rate" type="number" size="medium" :min="0.01" :step="0.01" :placeholder="$t('tool.CompoundInterest.phRate')" @input="calc">
          <template slot="append">%</template>
        </el-input>
      </div>
      <div class="ci-field">
        <span class="ci-field-lbl">{{ $t('tool.CompoundInterest.years') }}</span>
        <el-input-number v-model="years" :min="1" :max="30" size="medium" style="width:100%" @change="calc"/>
      </div>
      <div class="ci-field">
        <span class="ci-field-lbl">{{ $t('tool.CompoundInterest.frequency') }}</span>
        <el-select v-model="frequency" size="medium" style="width:100%" @change="calc">
          <el-option :label="$t('tool.CompoundInterest.yearly')" :value="1"/>
          <el-option :label="$t('tool.CompoundInterest.monthly')" :value="12"/>
          <el-option :label="$t('tool.CompoundInterest.daily')" :value="365"/>
        </el-select>
      </div>
    </div>

    <!-- 结果 -->
    <template v-if="result">
      <div class="ci-cards">
        <div class="ci-card ci-card-p">
          <div class="ci-card-hd">{{ $t('tool.CompoundInterest.legendPrincipal') }}</div>
          <div class="ci-card-val">{{ fmt(principal) }}</div>
          <div class="ci-card-unit">{{ $t('tool.CompoundInterest.unitYuan') }}</div>
        </div>
        <div class="ci-card ci-card-i">
          <div class="ci-card-hd">{{ $t('tool.CompoundInterest.totalInterest') }}</div>
          <div class="ci-card-val">{{ result.totalInterest }}</div>
          <div class="ci-card-unit">{{ $t('tool.CompoundInterest.unitYuan') }} · {{ $t('tool.CompoundInterest.effRate') }} {{ result.effRate }}</div>
        </div>
        <div class="ci-card ci-card-t">
          <div class="ci-card-hd">{{ $t('tool.CompoundInterest.total') }}</div>
          <div class="ci-card-val">{{ result.finalAmount }}</div>
          <div class="ci-card-unit">{{ $t('tool.CompoundInterest.unitYuan') }}</div>
        </div>
      </div>

      <!-- 逐年增长柱状图 -->
      <div class="ci-chart" v-if="result.yearlyData.length <= 20">
        <div class="ci-chart-title">{{ $t('tool.CompoundInterest.chartTitle') }}</div>
        <div class="ci-bars">
          <div class="ci-bar-col" v-for="d in result.yearlyData" :key="d.year">
            <div class="ci-bar-stack">
              <div class="ci-bar ci-bar-principal" :style="{height:barPct(d,true)+'%'}" :title="$t('tool.CompoundInterest.legendPrincipal')+' '+fmt(d.start)+$t('tool.CompoundInterest.unitYuan')"/>
              <div class="ci-bar ci-bar-interest" :style="{height:barPct(d,false)+'%'}" :title="$t('tool.CompoundInterest.legendInterest')+' '+fmt(d.interest)+$t('tool.CompoundInterest.unitYuan')"/>
            </div>
            <span class="ci-bar-label" v-if="showLabel(d.year)">{{ $t('tool.CompoundInterest.yearLabel',{y:d.year}) }}</span>
          </div>
        </div>
        <div class="ci-legend">
          <span><i class="ci-legend-dot ci-dot-p"/> {{ $t('tool.CompoundInterest.legendPrincipal') }}</span>
          <span><i class="ci-legend-dot ci-dot-i"/> {{ $t('tool.CompoundInterest.legendInterest') }}</span>
        </div>
      </div>

      <!-- 逐年明细表 -->
      <el-collapse class="ci-collapse">
        <el-collapse-item>
          <template slot="title"><span class="ci-collapse-title">{{ $t('tool.CompoundInterest.detailTitle',{n:years}) }}</span></template>
          <div class="ci-table-wrap">
            <table class="ci-table">
              <thead><tr><th>{{ $t('tool.CompoundInterest.colYear') }}</th><th>{{ $t('tool.CompoundInterest.colStart') }}</th><th>{{ $t('tool.CompoundInterest.colInterest') }}</th><th>{{ $t('tool.CompoundInterest.colEnd') }}</th></tr></thead>
              <tbody>
                <tr v-for="d in result.yearlyData" :key="d.year">
                  <td>{{ d.year }}</td><td>{{ d.startText }}</td><td>{{ d.interestText }}</td><td>{{ d.endText }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </el-collapse-item>
      </el-collapse>
    </template>

    <div class="fmt-status-bar" v-else>
      <span class="fmt-hint">{{ $t('tool.CompoundInterest.emptyHint') }}</span>
    </div>
  </div>
</template>

<script>
function fmt(n) {
  if (typeof n !== 'number' || isNaN(n)) return '0'
  return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export default {
  name:'CompoundInterestTool',
  props:{ config:{ type:Object, default:function(){ return {} } } },
  data:function(){
    return { principal:100000, rate:3.5, years:10, frequency:12, result:null }
  },
  created:function(){ this.calc() },
  methods:{
    fmt:function(n){ return fmt(n) },
    showLabel:function(y){
      if (this.years <= 10) return true
      if (this.years <= 20) return y % 2 === 1
      return y % 5 === 1 || y === this.years
    },
    barPct:function(d,isPrincipal){
      var P = this.principal
      var max = this.result.yearlyData[this.result.yearlyData.length - 1].end
      if (isPrincipal) return (P / max * 100)
      return ((d.end - P) / max * 100)
    },
    calc:function(){
      var P = this.principal, r = this.rate, t = this.years, n = Number(this.frequency)
      if (!P||!r||!t||P<=0||r<=0||t<=0){this.result=null;return}
      var A = P * Math.pow(1 + r/100/n, n * t)
      var effRate = ((Math.pow(1 + r/100/n, n) - 1) * 100).toFixed(4) + '%'
      var yearlyData = []
      for (var y = 1; y <= t; y++) {
        var endVal = P * Math.pow(1 + r/100/n, n * y)
        var startVal = y===1?P:P*Math.pow(1+r/100/n,n*(y-1))
        yearlyData.push({
          year:y,start:startVal,end:endVal,interest:endVal-startVal,
          startText:fmt(startVal),endText:fmt(endVal),interestText:fmt(endVal-startVal)
        })
      }
      this.result = { finalAmount:fmt(A), totalInterest:fmt(A-P), effRate:effRate, yearlyData:yearlyData }
    },
    clearAll:function(){ this.principal=100000;this.rate=3.5;this.years=10;this.frequency=12;this.result=null },
    doAction:function(a){ if(a==='clear')this.clearAll() }
  }
}
</script>

<style scoped>
.fmt-tool{display:flex;flex-direction:column;gap:16px;max-width:100%}

.fmt-toolbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.fmt-toolbar >>> .el-button + .el-button{margin-left:0}
.fmt-label{font-size:13px;font-weight:700;color:#1a1f36}
.fmt-spacer{flex:1}

/* 表单 */
.ci-form{display:grid;grid-template-columns:repeat(4,1fr);gap:14px}
.ci-field{display:flex;flex-direction:column;gap:4px}
.ci-field-lbl{font-size:12px;font-weight:600;color:#909399}

/* 结果卡片 */
.ci-cards{display:flex;gap:14px}
.ci-card{flex:1;border-radius:12px;padding:20px;text-align:center;border:1px solid #ebeef5}
.ci-card-p{background:#f8f9fb}
.ci-card-i{background:#f0fdf4;border-color:#c2e7b0}
.ci-card-t{background:#f0f4ff;border-color:#c5d4f0}
.ci-card-hd{font-size:12px;font-weight:600;color:#909399;margin-bottom:8px}
.ci-card-val{font-size:24px;font-weight:700;font-family:'SF Mono',Menlo,Consolas,monospace;color:#1a1f36;word-break:break-all}
.ci-card-i .ci-card-val{color:#43a047}
.ci-card-t .ci-card-val{color:#4e7cff}
.ci-card-unit{font-size:12px;color:#b0b8c4;margin-top:4px}

/* 柱状图 */
.ci-chart{background:#fff;border:1px solid #ebeef5;border-radius:12px;padding:18px 20px}
.ci-chart-title{font-size:13px;font-weight:700;color:#303133;margin-bottom:14px}
.ci-bars{display:flex;align-items:flex-end;gap:3px;height:160px;padding-bottom:20px}
.ci-bar-col{flex:1;display:flex;flex-direction:column;align-items:center;height:100%;min-width:0}
.ci-bar-stack{flex:1;width:100%;display:flex;flex-direction:column;justify-content:flex-end;gap:0}
.ci-bar{border-radius:2px 2px 0 0;transition:height .3s}
.ci-bar-principal{background:#4e7cff;opacity:.7}
.ci-bar-interest{background:#43a047;opacity:.7}
.ci-bar-label{font-size:9px;color:#b0b8c4;margin-top:4px;white-space:nowrap}
.ci-legend{display:flex;gap:16px;font-size:11px;color:#909399;margin-top:8px}
.ci-legend-dot{display:inline-block;width:10px;height:10px;border-radius:2px;margin-right:4px}
.ci-dot-p{background:#4e7cff;opacity:.7}
.ci-dot-i{background:#43a047;opacity:.7}

/* 明细表 */
.ci-collapse >>> .el-collapse-item__header{font-weight:600;color:#606266;font-size:13px}
.ci-collapse-title{font-size:13px}
.ci-table-wrap{max-height:400px;overflow:auto;border:1px solid #ebeef5;border-radius:10px}
.ci-table{width:100%;border-collapse:collapse;font-size:13px}
.ci-table th,.ci-table td{padding:8px 12px;text-align:right;border-bottom:1px solid #ebeef5}
.ci-table th{background:#fafbfc;color:#606266;font-weight:600;position:sticky;top:0;font-size:12px}
.ci-table td{color:#303133;font-family:monospace}
.ci-table td:first-child{text-align:center;color:#606266;font-family:inherit}

.fmt-status-bar{font-size:12px}
.fmt-hint{color:#c0c4cc}

@media(max-width:768px){.ci-form{grid-template-columns:repeat(2,1fr)}.ci-cards{flex-direction:column}}
@media(max-width:480px){.ci-form{grid-template-columns:1fr}}
</style>
